import { useRef, useState } from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import { ConfirmDialog, DialogSurface, FlowSteps, FormTextField, ResourceState, ResultState } from '../../../design-system';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { getFixtureBulkOperation, saveFixtureBulkOperation, type BulkOperationReceipt, type BulkOperationsPort } from '../../bulk/public';
import type { PeopleOperationsPort } from '../operations/port';
import type { PeopleWorker } from '../operations/model';
import { fixturePeopleHireReferences } from '../operations/fixtures';
import { buildStaffingRows, getStaffingCurrentValue, isStaffingDraftValid, staffingNextValue, staffingSelectedFields, type StaffingChangeDrafts } from '../model/peopleBulkChanges';
import { usePeopleStaffingReferences } from '../model/usePeopleStaffingReferences';
import { PeopleBulkChangeRecipients } from './PeopleBulkChangeRecipients';
import { PeopleBulkChangeReview, type StaffingChangePreview } from './PeopleBulkChangeReview';

export function PeopleBulkChangeDialog({ workers: initialWorkers, availableWorkers, asOf, port, bulkPort, fixtureMode, onClose, onOpenJob }: {
  workers: PeopleWorker[];
  availableWorkers?: PeopleWorker[];
  asOf: string;
  port: PeopleOperationsPort;
  bulkPort: BulkOperationsPort;
  fixtureMode: boolean;
  onClose: () => void;
  onOpenJob: (id: string, receipt: BulkOperationReceipt) => void;
}) {
  const [workers, setWorkers] = useState(initialWorkers);
  const [date, setDate] = useState(asOf);
  const [drafts, setDrafts] = useState<StaffingChangeDrafts>({});
  const [commonEditing, setCommonEditing] = useState(false);
  const [commonDirty, setCommonDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [referenceDateChanged, setReferenceDateChanged] = useState(false);
  const [reason, setReason] = useState('');
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [job, setJob] = useState<{ id: string; submitted: boolean; receipt: BulkOperationReceipt }>();
  const [key, setKey] = useState(() => crypto.randomUUID());
  const primaryAction = useRef<HTMLButtonElement>(null);
  const referenceResource = useAsyncResource(async () => ({ date, values: fixtureMode ? fixturePeopleHireReferences : await port.listHireReferences(date) }), [port, fixtureMode, date]);
  // 새 날짜의 요청이 시작되기 전 렌더에서도 이전 날짜의 선택지를 제출에 사용하지 않는다.
  const references = { ...referenceResource, data: referenceResource.data?.date === date ? referenceResource.data.values : undefined,
    loading: referenceResource.loading || (!referenceResource.error && referenceResource.data?.date !== date) };
  const extraReferences = usePeopleStaffingReferences(port, fixtureMode, date);
  const staffingReferences = references.data ? { ...references.data, jobTypes: extraReferences.jobTypes, workSpaces: extraReferences.workSpaces, managers: extraReferences.managers } : undefined;
  const rows = buildStaffingRows(workers, drafts, date, reason);
  const valid = workers.length > 0 && workers.length <= 1000 && /^\d{4}-\d{2}-\d{2}$/.test(date)
    && workers.every((worker) => isStaffingDraftValid(drafts[worker.id] ?? {}, staffingReferences, [worker]))
    && Boolean(reason.trim()) && reason.trim().length <= 4000 && !references.loading && !references.error && !commonEditing;
  const preview: StaffingChangePreview[] = workers.map((worker) => ({
    worker,
    changes: staffingSelectedFields(drafts[worker.id] ?? {}).map((field) => {
      const value = drafts[worker.id][field.key]?.trim() ?? '';
      return { label: field.previewLabel, before: getStaffingCurrentValue(worker, field.key), after: staffingNextValue(field.key, value, staffingReferences, [worker], drafts[worker.id]) || '이름 미설정' };
    }),
  }));
  const dirty = workers.length !== initialWorkers.length || workers.some(worker => !initialWorkers.some(initial => initial.id === worker.id)) || date !== asOf || Boolean(reason) || commonDirty || Object.values(drafts).some((draft) => staffingSelectedFields(draft).length > 0);
  const requestClose = () => {
    if (busy) return;
    if (!job && dirty) setConfirmClose(true);
    else onClose();
  };
  const review = () => { if (!valid) return; setKey(crypto.randomUUID()); setStep(1); };
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setError('');
    try {
      const receipt: BulkOperationReceipt = job?.receipt ?? {
        effectiveDate: date,
        rows: preview.map(({ worker, changes }, index) => ({
          rowNumber: index + 1,
          name: worker.name,
          subtitle: [worker.jobProfile || worker.title, worker.organization].filter(Boolean).join(' · '),
          avatarUrl: worker.photoUrl,
          changes,
        })),
      };
      let id = job?.id;
      if (!id) {
        const created = fixtureMode ? saveFixtureBulkOperation({
          id: `fixture-bulk-change-${key}`, operationType: 'STAFFING_CHANGE', originalFilename: '선택 구성원 인사 발령',
          status: 'VALIDATED', validRows: rows.length, totalRows: rows.length, succeededRows: 0, failedRows: 0,
          createdAt: new Date().toISOString(), rows: rows.map((_, index) => ({ rowNumber: index + 1, status: 'VALID' })),
        }) : await bulkPort.createBulkOperation('STAFFING_CHANGE', '선택 구성원 인사 발령', rows, key);
        if (created.validRows !== created.totalRows) throw new Error(created.rows.filter((row) => row.status === 'INVALID').map((row) => `${row.rowNumber}행: ${row.errorMessage}`).join('\n') || '입력 오류를 확인해 주세요.');
        id = created.id; setJob({ id, submitted: false, receipt });
      }
      if (fixtureMode) {
        const created = getFixtureBulkOperation(id);
        if (!created) throw new Error('샘플 발령 작업을 찾을 수 없습니다. 다시 시도해 주세요.');
        saveFixtureBulkOperation({ ...created, status: 'QUEUED', submittedAt: new Date().toISOString() });
      } else await bulkPort.submitBulkOperation(id, `${key}:submit`);
      setJob({ id, submitted: true, receipt }); setStep(2);
    } catch (cause) { setError(cause instanceof Error ? cause.message : '발령 요청을 제출하지 못했습니다. 같은 요청으로 재시도해 주세요.'); }
    finally { setBusy(false); }
  };
  return <><DialogSurface open onClose={requestClose} busy={busy} title="선택 구성원 일괄 발령" size="large" mobilePresentation="fullscreen"
    headerContent={<FlowSteps ariaLabel="일괄 발령 단계" activeStep={step} steps={['변경 내용', '확인·제출', '요청 완료']} />}
    actions={step === 2 ? <Button onClick={onClose}>닫기</Button> : <><Button disabled={busy} onClick={requestClose}>닫기</Button>{step === 1 && !job && <Button disabled={busy} onClick={() => {
      setStep(0); requestAnimationFrame(() => primaryAction.current?.focus());
    }}>이전</Button>}<Button ref={primaryAction} variant="fill" disabled={busy || !valid} onClick={() => step === 0 ? review() : void submit()}>{busy ? '제출 중' : step === 0 ? '변경 내용 확인' : job ? '같은 요청 재시도' : `${workers.length}명 발령 승인 요청`}</Button></>}>
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {job && !job.submitted && <Alert severity="info">발령 작업이 생성되었습니다. 같은 작업으로 제출을 재시도하거나 처리 현황을 열어 진행 상태를 확인할 수 있습니다.<Button onClick={() => onOpenJob(job.id, job.receipt)}>처리 현황 보기</Button></Alert>}
      {step === 2 ? <ResultState embedded tone="success" title="발령 처리 요청 완료" description="구성원별 발령을 순서대로 제출하고 설정된 결재 방식에 따라 처리합니다. 처리 현황에서 결과와 실패한 행을 확인할 수 있습니다." action={<Button variant="fill" onClick={() => job && onOpenJob(job.id, job.receipt)}>처리 현황 보기</Button>} /> : step === 0 ? <>
        {workers.length > 1000 && <Alert severity="warning">한 번에 최대 1,000명까지 요청할 수 있습니다.</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '200px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
          <FormTextField label="시행일" type="date" required value={date} onChange={(event) => {
            const value = event.target.value;
            if (value === date) return;
            setDate(value);
            setReferenceDateChanged(true);
          }} />
          <FormTextField label="발령 사유" required multiline minRows={1} value={reason} onChange={(event) => setReason(event.target.value)} characterCount={{ current: reason.length, max: 4000 }} helperText={reason.length > 4000 ? '4,000자 이하로 입력해 주세요.' : undefined} error={reason.length > 4000} fullWidth />
        </Box>
        {(references.loading || references.error) && <ResourceState embedded density="compact" empty={false} loading={references.loading} error={references.error?.message} onRetry={references.reload}>{null}</ResourceState>}
        {extraReferences.error && <Alert severity="error" action={<Button onClick={extraReferences.reload}>다시 시도</Button>}>고용 유형 또는 업무 공간을 조회하지 못했습니다.</Alert>}
        <Box component="fieldset" disabled={references.loading || Boolean(references.error)} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
          <PeopleBulkChangeRecipients workers={workers} availableWorkers={availableWorkers ?? initialWorkers} onWorkersChange={setWorkers} drafts={drafts} onChange={setDrafts} references={staffingReferences} onRequireReferences={extraReferences.request}
            referenceDateChanged={referenceDateChanged} onCommonEditingChange={setCommonEditing} onCommonDirtyChange={setCommonDirty} />
        </Box>
      </> : <PeopleBulkChangeReview preview={preview} date={date} reason={reason} asOf={asOf} />}
    </Stack>
  </DialogSurface>
    <ConfirmDialog open={confirmClose} title="입력한 내용을 버릴까요?" description="아직 제출하지 않은 발령 내용이 사라집니다."
      confirmLabel="버리고 닫기" cancelLabel="계속 편집" intent="discard" onCancel={() => setConfirmClose(false)} onConfirm={onClose} />
  </>;
}
